import * as React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Md5 } from 'ts-md5/dist/md5';
import { Modify } from '../../common/utils';
import { StatusIndicator } from '../StatusIndicator';
import { AvatarSizeName, ColorSet, getAvatarSize, seededRandomColorSet } from "--components/theme";
import { AvatarLabelFromDisplayName } from '--client/common/utils';
import { UserPresenceValue } from '--client/../static_types/presence.types';

import Link from "@floji-internal/mui-icons/Link"
import { ConditionalWrapper } from '../ConditionalWrapper';
import { getAssetUrl } from '../../../../producer/components/Assets/utils';
import IcoCheck from "@floji-internal/mui-icons/Check";

// Sizes are defaults taken from MUI 
// May want to elevate these up to the theme level

// Todo: add status indicator to avatar rather than having seperate statusIndicator component
type AvatarVariant = "outline" | "multicoloured"
type AvatarProps = Modify<React.HtmlHTMLAttributes<HTMLDivElement>, {
    src?: string,
    srcset?: string,
    gravatarEmail?: string,
    gravatarMD5?: string,
    avatar_asset_id?: string,
    variant?: AvatarVariant,
    colorSeed?: string,
    initials?: string,
    square?: boolean | "half",
    displayName?: string
    alt?: string
    size?: AvatarSizeName
    external?: boolean,
    status?: UserPresenceValue,
    classes?: {
        container?: string
        image?: string,
        initials?: string,
    },
    disabled?: boolean
    colorSet?: ColorSet | null,
    icon?: React.ReactElement,
    checked?: boolean
}>


//TODO , useImageLoaded needs replaced with a cached fetch through the fetch API
//gravatars are slow to load and many many network requests are processed, 
//since gravatar doesn't send sensible cache headers!


export const DummyAvatar = styled.div<{ size: number, disabled?: boolean }>`
    width: ${props => props.size}px;
    height: ${props => props.size}px;
    border-radius: 50%;
    background: ${props => props.theme.palette.surface};
`;

const Container = styled.div<{ variant?: AvatarVariant, size: number, disabled?: boolean, colorSet?: ColorSet | null, hasImage?: boolean, square?: boolean | "half", checked?: boolean }>`
    position: relative;
    width: ${props => props.size}px;
    height: ${props => props.size}px;
    overflow: hidden;
    border-radius: ${props => props.square === "half" ?
        `0px 0px max(${props.theme.radius.small}, 5%) max(${props.theme.radius.small}, 5%)`
        : props.square ? `max(${props.theme.radius.small}, 5%)` : "50%"};
    display: flex;
    flex: 0 0 auto;
    justify-content: center;
    align-items: center;
    
    background: ${props =>
        props.variant === "outline" || props.variant === "multicoloured"
            ? "none"
            : props.colorSet === null
                ? "inherit"
                : props.colorSet
                    ? props.colorSet.main
                    : props.theme.palette.primary.main
    };

    ${props => props.variant == "outline" && css`
        box-shadow: 0 0 0 3px inset ${props.colorSet === null
            ? "inherit"
            : props.colorSet
                ? props.colorSet.main
                : props.theme.palette.primary.main};
    `}

    color: ${props => props.variant === "outline"
        ? props.theme.style === "light"
            ? `${props.colorSet === null
                ? "inherit" : props.colorSet?.main}`
            : `${props.colorSet === null ? "inherit"
                : props.colorSet?.contrast}`
        : props.theme.palette.primary.contrast
    };
      
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-size: ${props => props.size / 2}px;
    line-height: 1;
    user-select: none;
    cursor: ${props => props.onClick ? "pointer" : "initial"};
    filter: ${props => props.disabled ? "grayscale(100%)" : ""};

    &:after {
        display:block;
        position:absolute;
        width:100%;
        height:100%;
        box-shadow: ${props => props.theme.shadows.inset};
        border-radius: inherit;
        content:'';
    }
    
    &>img {
        color: transparent;
        width: 100%;
        height: 100%;
        object-fit: cover;

    }   
`;

const AvatarCheck = styled.div<{checked: boolean}>`
    position: absolute;
    display: flex; 
    align-items: center;
    justify-content: center;
    top: 0; left: 0; right: 0; bottom: 0;
    opacity: ${props => props.checked ? 1 : 0};
    background: ${props=> props.theme.palette.positive.main};
    color: ${props=> props.theme.palette.positive.contrast};

    transition: opacity ${props => props.theme.transitions.toggle};
`;

const StyledMultiColouredAvatar = styled.div`
    left:0;
    top:0;
    right:0;
    bottom:0;
    position: absolute;
`;
const StyledMultiColouredAvatarBlock = styled.div<{ index: number, color: string }>`
    width:50%;
    height:50%;
    position:absolute;
    top: ${p => p.index < 2 ? 0 : "50%"};
    left: ${p => p.index % 2 === 0 ? 0 : "50%"};
    background-color:${p => p.color};
`;

const MultiColouredAvatar: React.FC = props => {
    return <StyledMultiColouredAvatar>
        <StyledMultiColouredAvatarBlock index={0} color="red" />
        <StyledMultiColouredAvatarBlock index={1} color="green" />
        <StyledMultiColouredAvatarBlock index={2} color="blue" />
        <StyledMultiColouredAvatarBlock index={3} color="yellow" />
    </StyledMultiColouredAvatar>
}

const StatusMask = styled.div<{ size: number }>`
    mask-image: url(${props => `"data:image/svg+xml,%3Csvg viewBox='0 0 ${props.size} ${props.size}' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cmask id='hole'%3E%3Crect width='100%25' height='100%25' fill='%23fff' /%3E%3Ccircle cx='86%25' cy='86%25' r='8' fill='%23000' shape-rendering='geometricPrecision' /%3E%3C/mask%3E%3C/defs%3E%3Crect width='100%25' height='100%25' mask='url(%23hole)' /%3E%3C/svg%3E"`});
    mask-repeat: no-repeat;
    mask-mode: alpha;
`;
const LayoutContainer = styled.div`
    position:relative;
`;
const External = styled(Link)`
    position:absolute;
    z-index:1;
    right:0px;
    bottom:0px;
    background-color:${props => props.theme.palette.surfaceContrast};
    border-radius:50%;
`;

/**
 * Render an avatar component, supports gravatar
 */

const Avatar = React.forwardRef((props: AvatarProps, ref: React.Ref<HTMLDivElement>) => {
    const {
        gravatarEmail, gravatarMD5, src, srcset, initials, checked, icon,
        alt, size, classes, status, colorSeed, displayName, avatar_asset_id,
        ...containerProps
    } = props;

    const pxSize = getAvatarSize(size);   
    const [assetFile, setAssetFile] = React.useState<string | undefined>();
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const theme = useTheme();

    React.useEffect(() => { avatar_asset_id ? setAssetFile(`${window.location.origin}${getAssetUrl(avatar_asset_id)}`) : setAssetFile(undefined) }, [avatar_asset_id]);

    // Rated G avatar (see https://en.gravatar.com/site/implement/images/)
    // also, we use the blank png as a return, otherwise our logs are filled with 404s
    const gravatarUrl =
        (gravatarEmail ? `https://www.gravatar.com/avatar/${new Md5().appendStr(gravatarEmail).end()}?d=blank&r=g&s=` : undefined)
        || (gravatarMD5 ? `https://www.gravatar.com/avatar/${gravatarMD5 as string}?d=blank&r=g&s=` : undefined)
        || undefined;
    const imgSrc = assetFile
        || src
        || gravatarUrl
        || undefined;
    const imgSrcset = assetFile
        || srcset
        || (gravatarUrl ? `${gravatarUrl}${pxSize * 4} 4x, ${gravatarUrl}${pxSize * 3} 3x, ${gravatarUrl}${pxSize * 2} 2x, ${gravatarUrl}${pxSize} 1x` : undefined)
        || undefined;
    const text = initials?.toUpperCase() || displayName && AvatarLabelFromDisplayName(displayName).toUpperCase();
    const colorSet = props.colorSet || colorSeed && seededRandomColorSet(colorSeed) || undefined;

    return <LayoutContainer>
        {props.external ? <External size={Math.floor(pxSize / 3) + "px"} color={theme.palette.text.contrast} /> : null}
        <ConditionalWrapper
            condition={!!props.status}
            wrap={children =>
                <StatusIndicator {...props.status!} offset="circle" offsetPosition="bottom-right">
                    <StatusMask size={pxSize}>
                        {children}
                    </StatusMask>
                </StatusIndicator>
            }>
            <Container 
                ref={ref}
                variant={props.variant} 
                square={props.square}
                className={classes && classes.container}
                size={pxSize}
                colorSet={colorSet}
                checked={checked}
                hasImage={imageLoaded}
                {...containerProps}>           
                <img
                    onLoad={(e) => { setImageLoaded(true) }}
                    className={classes && classes.image}
                    style={{ position: "absolute", display: imageLoaded ? "block" : "none" }}
                    srcSet={imgSrcset}
                    src={imgSrc}
                    alt={alt} /> 
                

                {
                    /* 
                        Render in this order of preference:
                            - Icon
                            - Text (initials either from the initials prop or the display name)
                            - Multicolored 
                            - Children
                    */
                    icon
                        ? React.cloneElement(icon, {size: "60%"})
                        : text
                            ? <div className={classes && classes.initials}>{text}</div>
                            : props.variant === "multicoloured"
                                ? <MultiColouredAvatar />
                                : props.children
                }

                { checked !== undefined && <AvatarCheck checked={checked}>
                    <IcoCheck size="60%" />
                </AvatarCheck> }
            </Container>
        </ConditionalWrapper>
    </LayoutContainer>
})


export default Avatar;
export { Avatar, AvatarProps }